import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';

import BodyPortal from '../portals/body-portal';

import Hotkey from 'components/Hotkey';
import Tooltip from 'components/tooltip';

import IconFormatBold from '../icons/icon-format-bold';
import IconFormatStrike from '../icons/icon-format-strike';
import IconFormatItalic from '../icons/icon-format-italic';
import IconFormatOrderedList from '../icons/icon-format-ordered-list';
import IconFormatUnorderedList from '../icons/icon-format-unordered-list';
import IconFormatBlockquote from '../icons/icon-format-blockquote';
import IconFormatCode from '../icons/icon-format-code';
import IconFormatCodeBlock from '../icons/icon-format-code-block';

const WINDOW_PADDING = 10;

const Button = ({ action, shortcut, onClick, children }) => {
    const handleClick = event => {
        event.preventDefault();

        onClick && onClick(action);
    }

    return (
        <span onMouseDown={handleClick}>
            <Tooltip placement="top" tooltip={shortcut}>
                <button>
                    { children }
                </button>
            </Tooltip>
        </span>
    );
};

const hiddenStyle = {
    transform: 'scale(0)'
};

const EditorHoverMenu = ({ parentElement, focused, onClick, provideController }) => {
    const [visible, setVisible] = useState(false);
    const [position, setPosition] = useState([0, 0]);
    const ref = useRef();

    const update = useCallback(() => {
        const el = ref.current;

        if (!el) {
            return
        }

        const selection = window.getSelection();

        if (!focused || selection.isCollapsed || !parentElement.current.contains(selection.focusNode)) {
            setVisible(false);
            return
        }

        const domRange = selection.getRangeAt(0);
        const rect = domRange.getBoundingClientRect();
        const left = Math.min(Math.max(WINDOW_PADDING, rect.left + window.pageXOffset - el.offsetWidth / 2 + rect.width / 2), window.innerWidth - el.offsetWidth - WINDOW_PADDING);
        const top = rect.top + window.pageYOffset - el.offsetHeight;

        setVisible(true);
        setPosition([left, top]);
    }, [focused, parentElement]);

    const style = useMemo(() => {
        if (!visible) {
            return hiddenStyle;
        }

        return {
            left: position[0],
            top: position[1],
            opacity: 1
        }
    }, [visible, position]);

    useEffect(() => {
        update();
    }, [update]);

    const handleSelectionChange = useCallback(() => {
        update();
    }, [update]);

    useEffect(() => {
        const ctrl = {
            update
        };

        if (provideController) {
            provideController.current = ctrl;
        }

        return () => {
            if (provideController) {
                provideController.current = null;
            }
        }
    }, []);

    useEffect(() => {
        document.addEventListener('selectionchange', handleSelectionChange);

        return () => {
            document.removeEventListener('selectionchange', handleSelectionChange);
        }
    }, [handleSelectionChange]);

    return (
        <BodyPortal>
            <div className="input-text-editor-menu" ref={ref} style={style}>
                <Button shortcut={<Hotkey metaKey={true} keyName="B" />} action="bold" onClick={onClick}><IconFormatBold /></Button>

                <Button shortcut={<Hotkey metaKey={true} keyName="I" />} action="italic" onClick={onClick}><IconFormatItalic /></Button>

                <Button shortcut={<Hotkey metaKey={true} keyName="E" />} action="strikethrough" onClick={onClick}><IconFormatStrike /></Button>

                <Button shortcut={<Hotkey metaKey={true} shiftKey={true} keyName="C" />} action="code" onClick={onClick}><IconFormatCode /></Button>

                <Button shortcut={<Hotkey metaKey={true} keyName="L" />} action="ordered-list" onClick={onClick}><IconFormatOrderedList /></Button>

                <Button shortcut={<Hotkey metaKey={true} shiftKey={true} keyName="L" />} action="unordered-list" onClick={onClick}><IconFormatUnorderedList /></Button>

                <Button shortcut={<Hotkey metaKey={true} shiftKey={true} keyName="U" />} action="block-quote" onClick={onClick}><IconFormatBlockquote /></Button>

                <Button shortcut={<Hotkey metaKey={true} shiftKey={true} altKey={true} keyName="C" />} action="code-block" onClick={onClick}><IconFormatCodeBlock /></Button>
            </div>
        </BodyPortal>
    );
}

export default EditorHoverMenu;
